import React from 'react';
import { useTheme } from '@mui/material';
import { artworkFor } from '../../configuration/artwork';

/**
 * The M.E.-Kollektiv logo, placed as the artwork it is — exported from the
 * corporate identity file kept at docs/brand/Logo_Schrift.psd — so the drawn
 * letterforms, their spacing and the dotted O reach the page exactly as they
 * were made, rather than approximated in code.
 *
 * The approximation is still next door in LogoDrawn.tsx. It used to be the only
 * one of the two that followed the light/dark palette; the wordmark now has a
 * white export as well, looked up through configuration/artwork.ts, so this one
 * follows it too.
 *
 * Plain <img> rather than MUI's Box: Box reads `width` and `height` as style
 * props and swallows them, and it is the attributes that let the browser
 * reserve the logo's space before it has loaded.
 */

export type LogoVariant = 'full' | 'compact';

interface LogoProps {
  variant?: LogoVariant;
  /** Reference height in pixels, being the height of the full logo. */
  size?: number;
  /**
   * What the artwork is called for someone who cannot see it. Defaults to the
   * name it spells out; pass an empty string where the name is already in the
   * text around it, so a screen reader does not read it out twice.
   */
  alt?: string;
}

/**
 * The wordmark's height as a share of the full logo's, so that `size` means the
 * same thing to both variants: the height the full logo would have.
 *
 * Declared rather than derived from the two files' own proportions. The two
 * exports used to be cut from one artwork at one scale, and the share fell out
 * of their pixel heights; they are now cropped separately - the wordmark tight
 * around the letters - so their heights no longer stand in that relation and
 * deriving it would make the header logo twice the size it is meant to be.
 */
const WORDMARK_HEIGHT_SHARE = 90 / 259;

/**
 * The exports, with their own pixel sizes so the browser can hold the logo's
 * place while it loads.
 *
 * `full` carries the subtitle and exists only in the identity's anthracite.
 * Rather than stand it in dark grey on a dark ground, the dark view falls back
 * to the wordmark, which does have a white export - the subtitle is lost there,
 * and a light export of the full artwork is the one thing that would bring it
 * back. The subtitle is part of the picture and so is German on every page, as
 * it was when it was set as text.
 */
const LOGO_ASSETS = {
  full: {
    src: '/Logo_Schrift.png',
    hasWhiteExport: false,
    width: 960,
    height: 259,
    heightShare: 1,
    alt: 'M.E.-Kollektiv – DACH-Patientenorganisation Myalgische Enzephalomyelitis',
  },
  compact: {
    src: '/Logo_ohne_Schrift.png',
    hasWhiteExport: true,
    width: 1920,
    height: 191,
    heightShare: WORDMARK_HEIGHT_SHARE,
    alt: 'M.E.-Kollektiv',
  },
} as const;

export const Logo: React.FC<LogoProps> = ({ variant = 'full', size = 48, alt }) => {
  const mode = useTheme().palette.mode;
  const wanted = LOGO_ASSETS[variant];
  const asset = mode === 'dark' && !wanted.hasWhiteExport ? LOGO_ASSETS.compact : wanted;

  const height = size * asset.heightShare;
  const width = (height * asset.width) / asset.height;

  return (
    <img
      src={artworkFor(asset.src, mode)}
      alt={alt ?? asset.alt}
      width={Math.round(width)}
      height={Math.round(height)}
      // It sits in the header and the footer of every page: never the thing to
      // hold back for later.
      decoding="async"
      style={{ display: 'block', height, width: 'auto', maxWidth: '100%' }}
    />
  );
};
